/**
 * Safe storage helpers for restricted webviews (WhatsApp/Instagram/Facebook
 * in-app browsers on Android, private mode, etc.) where accessing
 * localStorage/sessionStorage may throw a SecurityError and blank the app.
 */

function createMemoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear() {
      map.clear();
    },
    getItem(key: string) {
      return map.has(key) ? (map.get(key) as string) : null;
    },
    key(index: number) {
      return Array.from(map.keys())[index] ?? null;
    },
    removeItem(key: string) {
      map.delete(key);
    },
    setItem(key: string, value: string) {
      map.set(key, String(value));
    },
  };
}

function probe(storage: Storage | undefined | null): Storage | null {
  if (!storage) return null;
  try {
    const testKey = '__probe__';
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return storage;
  } catch {
    return null;
  }
}

let _local: Storage | null | undefined;
let _session: Storage | null | undefined;
let _restricted: boolean | undefined;

export function getSafeLocalStorage(): Storage {
  if (_local === undefined) {
    try {
      _local = probe(typeof window !== 'undefined' ? window.localStorage : null);
    } catch {
      _local = null;
    }
  }
  return _local ?? createMemoryStorage();
}

export function getSafeSessionStorage(): Storage {
  if (_session === undefined) {
    try {
      _session = probe(typeof window !== 'undefined' ? window.sessionStorage : null);
    } catch {
      _session = null;
    }
  }
  return _session ?? createMemoryStorage();
}

export function isLocalStorageAvailable(): boolean {
  if (_local === undefined) getSafeLocalStorage();
  return _local !== null && _local !== undefined;
}

/**
 * True for known-fragile in-app browsers, or when localStorage is unusable.
 * Used to skip the persistent query cache and service-worker registration —
 * both of which have blanked the site on these webviews.
 */
export function isRestrictedWebView(): boolean {
  if (_restricted !== undefined) return _restricted;
  if (typeof navigator === 'undefined') {
    _restricted = false;
    return false;
  }
  const ua = navigator.userAgent || '';
  const uaHit = /(WhatsApp|FBAN|FBAV|Instagram|Line\/|MicroMessenger)/i.test(ua);
  const storageBroken = !isLocalStorageAvailable();
  _restricted = uaHit || storageBroken;
  return _restricted;
}
